import { useState, useEffect } from 'react'

import styles from './ManageTrustedUsersModal.module.scss'
import { withLocalize } from 'react-localize-redux'
import {
  InputAdornment,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox
} from '@material-ui/core'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useForm } from 'react-hook-form'
import cx from 'classnames'
import PropTypes from 'prop-types'
import AddEmailForm from 'common/components/AddEmailForm/AddEmailForm'
import ConfirmationModal from 'modules/modals/ConfirmationModal'
import Button from 'common/components/Button/Button'
import { EMAIL_REGEX } from 'utils/validation'

const ManageTrustedUsersModal = props => {
  const { translate, projects, user, isAddingEmail, handleSave, isSubmitting, userMail } = props

  const [attachedProjects, setAttachedProjects] = useState({})
  const [trustedUser, setTrustedUser] = useState([])
  const [selectedEmails, setSelectedEmails] = useState([])
  const [filteredTrustedUser, setFilteredTrustedUser] = useState([])
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)
  const [dataState, setDataState] = useState({})
  const [unsharedUsers, setUnsharedUsers] = useState({})
  const [usersToAdd, setUsersToAdd] = useState(new Set())
  const [openInfoE, setOpenInfoE] = useState(false)
  const { register, errors, setError, clearErrors, getValues, setValue, watch } = useForm()

  const handleAddUser = () => {
    const newEmail = (getValues('addressEmail') || '').trim().toLowerCase()
    const emailExist = trustedUser.some(user => user.email === newEmail)
    // all mails should be lowercase but just in case there is old registered data
    if (newEmail === userMail.toLowerCase()) {
      setError('userSelfAssign', { shouldFocus: 'addressEmail' })
    } else if (EMAIL_REGEX.test(newEmail) && !emailExist) {
      setFilteredTrustedUser([...filteredTrustedUser, { email: newEmail, confirmed: false }])
      setTrustedUser([...trustedUser, { email: newEmail, confirmed: false }])
      setUsersToAdd(prev => new Set(prev).add(newEmail))
      setValue('addressEmail', '')
    } else {
      setError('addressEmail', { shouldFocus: 'addressEmail' })
    }
  }

  const handleInfoE = () => {
    setOpenInfoE(!openInfoE)
  }

  useEffect(() => {
    const { trustedUser = [], unTrustedUser = [] } = user.toJS()
    const allEmails = [
      ...trustedUser.map(email => ({ email, confirmed: true })),
      ...unTrustedUser.map(email => ({ email, confirmed: false }))
    ].filter(({ email }) => email !== userMail)

    setTrustedUser(allEmails)
    setFilteredTrustedUser(allEmails)
  }, [user, userMail])

  useEffect(() => {
    const emailCount = {}
    trustedUser.forEach(user => {
      emailCount[user.email] = []
    })
    projects.forEach(project => {
      const projectCustomers = project.Meta.ProjectCustomers
      Object.values(projectCustomers).forEach(customer => {
        if (customer.email !== userMail) {
          const email = customer.email
          if (emailCount.hasOwnProperty(email)) {
            emailCount[email] = [...emailCount[email], project.ProjectId]
          }
        }
      })
    })

    setAttachedProjects(emailCount)
  }, [trustedUser, projects])

  const handleConfrimDeleteUsers = data => {
    const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
      if (value) acc[key] = value
      return acc
    }, {})
    const jsonData = {
      userId: user.toJS()._id,
      sharedProjects: filteredData
    }
    setDataState(jsonData)
    setOpenConfirmationModal(true)
  }

  const onConfirmSaveUser = () => {
    const emailsToDelete = Object.keys(dataState.sharedProjects || {})
    const updatedDeletedUsers = { ...unsharedUsers }
    const updatedAddedUsers = new Set(usersToAdd)

    emailsToDelete.forEach(email => {
      if (updatedAddedUsers.has(email)) {
        updatedAddedUsers.delete(email)
      } else {
        updatedDeletedUsers[email] = dataState.sharedProjects[email]
      }
    })

    setUnsharedUsers(updatedDeletedUsers)
    setUsersToAdd(updatedAddedUsers)
    setTrustedUser(prev => prev.filter(user => !emailsToDelete.includes(user.email)))
    setFilteredTrustedUser(prev => prev.filter(user => !emailsToDelete.includes(user.email)))
    setSelectedEmails([])
    setOpenConfirmationModal(false)
  }

  const handleFinalSave = () => {
    if (Array.from(usersToAdd).length > 0 || Object.keys(unsharedUsers).length > 0) {
      const finalPayload = {
        userId: user.toJS()._id,
        usersToAdd: Array.from(usersToAdd),
        unsharedUsers
      }
      handleSave(finalPayload)
    } else {
      setError('emails', 'required')
    }
  }

  const handleSelectEmail = e => {
    if (e.target.checked) {
      setSelectedEmails([...selectedEmails, e.target.value])
    } else {
      setSelectedEmails(selectedEmails.filter(email => email !== e.target.value))
    }
  }

  const handleSelectAllEmails = e => {
    if (e.target.checked) {
      const emails = trustedUser.map(user => user.email)
      setSelectedEmails(emails)
    } else {
      setSelectedEmails([])
    }
  }

  const onClickCancel = () => {
    props.hideModal()
  }

  const handleFilterExpressionChange = event => {
    const value = event.target.value
    if (value === '') {
      setFilteredTrustedUser(trustedUser)
    } else {
      const filtered = trustedUser.filter(user =>
        user.email.toLowerCase().includes(value.toLowerCase())
      )
      setFilteredTrustedUser(filtered)
    }
  }

  const handleDelete = emails => {
    if (emails.length > 0) {
      clearErrors('emails')
      const selectedEmailProjectIds = emails.reduce((acc, email) => {
        if (attachedProjects[email]) {
          acc[email] = attachedProjects[email]
        }
        return acc
      }, {})
      handleConfrimDeleteUsers(selectedEmailProjectIds)
    } else {
      setError('emails', 'required')
    }
  }

  return (
    <div>
      <div className={styles.modalContainer}>
        <Typography className={styles.header}>{translate('manageUsers')}</Typography>

        <div className={styles.modal}>
          <Typography className={styles.shareProject}>{translate('shareProjectInfo')}</Typography>

          <AddEmailForm
            title={translate('addUsersToProject')}
            register={register}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            handleInfoClick={handleInfoE}
            openInfo={openInfoE}
            closeInfo={() => setOpenInfoE(false)}
            addButtonLabel={translate('addUserToProject')}
            handleAddClick={handleAddUser}
            isAdding={isAddingEmail}
            translate={translate}
            getValues={getValues}
            watch={watch}
          />
          <form
            noValidate
            onKeyDown={ev => {
              if (ev.key === 'Enter') {
                ev.preventDefault()
              }
            }}
          >
            <Typography className={styles.subTitle}>{translate('selectUsers')}</Typography>
            <Typography className={styles.text}>{translate('checkUsers')}</Typography>
            <div className={styles.searchAddWrapper}>
              <TextField
                className={styles.searchBar}
                placeholder={translate('searchUser')}
                onChange={handleFilterExpressionChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchOutlinedIcon />
                    </InputAdornment>
                  )
                }}
                variant='outlined'
              />
            </div>
            <div className={styles.checkboxEmailWrapper}>
              <FormControlLabel
                className={styles.selectAllCheckbox}
                control={
                  <Checkbox
                    className={cx(styles.checkbox)}
                    classes={{ checked: styles.checkedBox }}
                    name='selectAllEmails'
                    id='selectAllEmails'
                    onChange={handleSelectAllEmails}
                  />
                }
                label={translate('selectAll')}
              />
              {filteredTrustedUser.map((email, i) => (
                <div
                  key={i + 'emailData'}
                  className={styles.checkboxFormDiv}
                >
                  <FormControlLabel
                    className={email.confirmed ? styles.confirmed : styles.notConfirmed}
                    control={
                      <Checkbox
                        className={cx(styles.checkbox)}
                        id={email.email}
                        classes={{ checked: styles.checkedBox }}
                        required
                        onChange={handleSelectEmail}
                        checked={selectedEmails.includes(email.email)}
                        name='emailRecipients'
                        value={email.email}
                        inputRef={register()}
                        disableRipple
                      />
                    }
                    label={email.email}
                  />
                  <Typography className={styles.emailCount}>
                    ({attachedProjects[email.email]?.length || 0}{' '}
                    {attachedProjects[email.email]?.length !== 1
                      ? translate('projectsInline')
                      : translate('projectInline')}
                    )
                  </Typography>
                </div>
              ))}
            </div>
            {errors.emails && (
              <div className={styles.error}>
                <small>{translate('validation.anyEmailRequired')}</small>
              </div>
            )}
            <Button
              type='light-delete'
              label={translate('deleteUsers')}
              onClick={() => handleDelete(selectedEmails)}
              disabled={isSubmitting}
              className={styles.deleteUsersButton}
            />
            <div className={styles.deleteUsersWarning}>
              <WarningAmberOutlinedIcon className={styles.deleteUsersWarningIcon} />
              <Typography className={styles.deleteUsersWarningText}>
                {translate('deleteUsersWarning')}
              </Typography>
            </div>
            <Button
              type='next'
              label={translate('save')}
              onClick={handleFinalSave}
              isLoading={isSubmitting}
              className={styles.saveButton}
            />
            <Button
              type='cancel'
              label={translate('addReportModal.cancel')}
              onClick={onClickCancel}
              disabled={isSubmitting}
              className={styles.cancelButton}
            />
          </form>
        </div>
      </div>
      {openConfirmationModal && (
        <ConfirmationModal
          hideModal={() => setOpenConfirmationModal(false)}
          translate={translate}
          contentText={translate('warningUsers')}
          onConfirm={() => onConfirmSaveUser(dataState)}
        />
      )}
    </div>
  )
}

ManageTrustedUsersModal.propTypes = {
  translate: PropTypes.func.isRequired
}

export default withLocalize(ManageTrustedUsersModal)
